
    import theme from '@/assets/theme.scss';
import Alert from '@/components/Alert';
import { backendExceptionHandler, chartIntervalStateHandler, companyLogoHandler } from '@/mixins';
import ChartService from '@/service/ChartService';
import FormatterService from "@/service/FormatterService";
import _ from 'lodash';
import Vue from 'vue';
import Searchbar from "../Searchbar";

    const REFRESH_PERIOD_MS = process.env.AREA_CHART_REFRESH_PERIOD || 300000  // 5 minutes

    let createChart, TickMarkType
    if (process.client) {
        const lightweight = require('lightweight-charts')
        createChart = lightweight.createChart
        TickMarkType = lightweight.TickMarkType
    }

    export const chartTypes = [
        {
            key: 'value',
            text: 'component.chart.areaChart.valueChange.text',
            tooltip: 'component.chart.areaChart.valueChange.tooltip',
        },
        {
            key: 'performance',
            text: 'component.chart.areaChart.valueChangePercent',
            tooltip: 'component.chart.areaChart.percentageChange.tooltip'
        }
    ]

    export const chartTypesSimpleReturn = [
        {
            key: 'simpleReturn',
            text: 'component.chart.areaChart.capitalGain',
            tooltip: 'component.chart.areaChart.simpleReturn.tooltip'
        },
        {
            key: 'simpleReturnRel',
            text: 'component.chart.areaChart.capitalGainPercent',
            tooltip: 'component.chart.areaChart.simpleReturnRel.tooltip'
        }
    ]

    export default {
        name: "Chart",
        mixins: [backendExceptionHandler, chartIntervalStateHandler, companyLogoHandler],
        components: {
            Searchbar,
            Alert,
        },

        props: {
            interval: String,
            intervals: Array,
            chartType: String,
            source: String,
            sourceIdentifier: String,
            sourceName: String,
            sourceLogo: String,
            instrumentType: {
                type: String,
                default: null
            },
            currency: String,
            isVisible: {
                type: Boolean,
                default: false,
            },
            refreshSwitch: Boolean,
            priceType: {
                type: String,
                default: ''
            },
            isAssetPortfolio: {
                type: Boolean,
                default: false
            },
            assetGroupKey: {
                type: String,
                default: ''
            },
            hideBaseline: {
                type: Boolean,
                default: false
            },
            defaultBenchmarks: {
                type: Array,
                default: () => []
            },
            isFinAgentChart: {
                type: Boolean,
                default: false
            },
            chartId: {
                type: String,
                default: 'chart'
            },
            initBenchmarks: {
                type: Array,
                default: () => []
            },
            chartHeight: {
                type: Number,
                default: 395,
            },
            intervalSelectorHeight: {
                type: String,
                default: '3em',
            },
            updateIntervalToPreferences: {
                type: Boolean,
                default: true,
            },
            showErrorMessage: {
                type: Boolean,
                default: true,
            },
            showBenchMarks: {
                type: Boolean,
                default: true
            },
        },

        data() {
            return {
                errorMessage: '',
                benchmarkError: null,
                isLoading: true,
                performanceRel: null,
                performanceAbs: null,
                chart: null,
                series: [],
                periodFrom: null,
                periodTo: null,
                selectIsinMode: false,
                benchmarks: [],
                displayBenchmarks: [],
                sourceLineColor: 'rgb(0, 180, 150)',
                chartTypes: chartTypes,
                chartTypesSimpleReturn: chartTypesSimpleReturn,
                updateBenchmarks: true,
                lastData: {},
                hasChartData: false,
                innerChartType: this.chartType,
                isInitializing: true,
                tooltip: { visible: false },
                // the format should be reactive in data option
                format: new FormatterService(),
                assetMethodsIcons: {}
            }
        },

        computed: {
            incompleteDatapoints() {
                return this.lastData.data?.incompleteDatapoints || []
            },
            colors() {
                return [
                    theme['chart-graph-color-1'],
                    theme['chart-graph-color-2'],
                    theme['chart-graph-color-3'],
                    theme['chart-graph-color-4'],
                    theme['chart-graph-color-5'],
                    theme['chart-graph-color-6'],
                    theme['chart-graph-color-7'],
                    theme['chart-graph-color-8'],
                    theme['chart-graph-color-9'],
                    theme['chart-graph-color-10'],
                    theme['chart-graph-color-11'],
                    theme['chart-graph-color-12'],
                    theme['chart-graph-color-13'],
                    theme['chart-graph-color-14'],
                    theme['chart-graph-color-15'],
                    theme['chart-graph-color-16'],
                ]
            },

            chartTypeSelection() {
                if (this.source == 'portfolio') {
                    return [...chartTypes, ...chartTypesSimpleReturn].map(item => {
                        return {
                            ...item,
                            text: this.$t(item.text),
                            tooltip: this.isDiabledButton(item.key) 
                                ? this.$t('component.chart.areaChart.absoluteChangeOnlySingleCharts') 
                                : this.$t(item.tooltip),
                            disabled: this.isDiabledButton(item.key)
                        }
                    })
                }
                return chartTypes.map(item => {
                    return {
                        ...item,
                        text: this.$t(item.text),
                        tooltip: this.isDiabledButton(item.key)
                                ? this.$t('component.chart.areaChart.absoluteChangeOnlySingleCharts')
                                : this.$t(item.tooltip),
                        disabled: this.isDiabledButton(item.key)
                    }
                });
            },
            chartStyleLight() {
                return {
                    layout: {
                        background: { color: 'transparent' },
                        textColor: theme['text-primary-light'],
                    },
                    timeScale: {
                        borderColor: theme['border-secondary-light'],
                    },
                    rightPriceScale: {
                        borderColor: theme['border-secondary-light'],
                    }
                }
            },
            chartStyleDark() {
                return {
                    layout: {
                        background: { color: 'transparent' },
                        textColor: theme['text-primary-dark'],
                    },
                    timeScale: {
                        borderColor: theme['border-secondary-dark'],
                    },
                    rightPriceScale: {
                        borderColor: theme['border-secondary-dark'],
                    }
                }
            },
            isDarkMode() {
                return this.$vuetify.theme.dark
            },
            isMarketAnalysisChart() {
                return this.source === 'market'
            },
            isSandboxMode() {
            return this.$store.state.preferences.isSandboxMode;
            },
            isPrivacyEnabled() {
                return this.$store.state.preferences.isPrivacyEnabled;
            },
            openLinkIconPath() {
                return this.$vuetify.theme.dark ?
                    require('@/assets/icons/open-link-dark.svg') :
                    require('@/assets/icons/open-link-light.svg');
            },
            deleteIconPath() {
                return this.$vuetify.theme.dark ?
                    require('@/assets/icons/delete-dark.svg') :
                    require('@/assets/icons/delete-light.svg');
            },
            eyeOffIconPath() {
                return this.$vuetify.theme.dark ?
                    require('@/assets/icons/eye-off-dark.svg') :
                    require('@/assets/icons/eye-off-light.svg');
            },
            eyeOnIconPath() {
                return this.$vuetify.theme.dark ?
                    require('@/assets/icons/eye-on-dark.svg') :
                    require('@/assets/icons/eye-on-light.svg');
            },
            closeIconPath() {
                return this.$vuetify.theme.dark ?
                    require('@/assets/icons/close-dark.svg') :
                    require('@/assets/icons/close-light.svg');
            },
            showChartTypeSelection() {
                return !this.isMarketAnalysisChart && !this.isFinAgentChart
            },
            showButtonGroup() {
                return this.showBenchMarks && !this.selectIsinMode && !this.isFinAgentChart
            },
            chartLocale() {
                const chartLocaleMapping = {
                    'en': 'en-CA',
                    'de': 'de-DE',
                    'fr': 'fr-FR',
                    'es': 'es-ES',
                };

                const currentLocale = this.$getCurrentLocale();
                return chartLocaleMapping[currentLocale]
            },
        },

        methods: {

            /**
             * @param source Source of data (portfolio/share)
             * @param identifier ISIN
             * @param dateTimeFrom
             * @param dateTimeTo
             * @returns {Promise<null|*>}
             */
            async getData(source, identifier, comparisons) {
                this.errorMessage = ''
                try {
                    this.activeRequestGetChartData = this.$axios.CancelToken.source();
                    this.lastData = {
                        identifier,
                        chartType: this.innerChartType,
                        interval: this.interval,
                        data: await this.chartService.get({
                            source: source,
                            sourceIdentifier: identifier,
                            interval: this.interval,
                            comparisons,
                            axiosSource: this.activeRequestGetChartData,
                        }, this.$t)
                    }
                    this.lastData.times = this.createTimesMap(this.lastData.data?.graphs?.[0]);
                    this.lastData.comparisonsTimesMap = this.createComparisonTimesMap(this.lastData.data?.graphs?.slice(1));
                    this.hasChartData = this.hasChartData || this.lastData.data?.graphs?.[0].datapoints.length || false
                    if (this.callClear) {
                        this.clear()
                        this.callClear = false
                    }
                    if (this.lastData.data?.graphs.reduce((prev, curr) => Math.max(prev, curr.datapoints.length), 0) < 1) {
                        this.lastData.data.graphs.forEach(g => g.datapoints = [])
                        this.hasChartData = false
                    }

                    this.isInitializing = false
                    if (!this.lastData.data) {
                        return null
                    }
                    return this.lastData.data
                } catch (e) {
                    //  if the error is request cancelation, need to keep the loading
                    if (this.$axios.isCancel(e)) {
                        throw e
                    }
                    this.isInitializing = false
                    this.hasChartData = false
                    this.isLoading = false
                    this.clear()
                    this.handleBackendException(e, this.$t('component.chart.areaChart.chartDataLoadFailed'))
                    return null
                }
            },

            createTimesMap(graph) {
                return graph?.datapoints.reduce((prev, curr) => {
                    const ts = this.getTimezoneCorrectedTime(curr.t, true);
                    prev[ts] = curr;

                    return prev;
                }, {}) ?? [];
            },

            createComparisonTimesMap(graphs) {
                return graphs?.map((graph, index) => {
                    return {
                        graphName: graph.graphName,
                        graphType: graph.graphType,
                        isin: graph.isin,
                        color: this.displayBenchmarks[index].color,
                        times: this.createTimesMap(graph)
                    }
                }) ?? []
            },

            getComparisonInfoByTime(data, time) {
                const timeData = data.times[time]
                return {
                    graphName: data.graphName,
                    isin: data.isin,
                    color: data.color,
                    value: timeData ? this.format.currency(timeData.c, this.currency) : null,
                    periodStartValue: timeData ? this.format.currency(timeData.v, this.currency) : null,
                    navChange: timeData ? this.format.currency(timeData.n, this.currency) : null,
                    navChangeRel: timeData ? this.format.percent(timeData.nr) : null,
                    simpleReturn: timeData ? this.format.currency(timeData.s, this.currency) : null,
                    simpleReturnRel: timeData ? this.format.percent(timeData.p) : null,
                }
            },

            clearActiveRefreshTimeout() {
                if (this.timeout) {
                    clearTimeout(this.timeout)
                    this.timeout = null
                }
            },

            setRefreshTimeout() {
                // reschedule only if the component was not destroyed during the last time lapse
                this.timeout =  this._isDestroyed === false ? setTimeout(this.handleRefreshChartAndKpi, REFRESH_PERIOD_MS) : null
            },

            async refresh(isChartTypeSwitch = false) {
                if (!this.isVisible) {
                    return
                }
                this.isLoading = true
                if (!this.sourceIdentifier || !this.interval) {
                    return
                }

                this.clearActiveRefreshTimeout()

                //clear all series before draw chart
                this.displayBenchmarks.forEach(share => {
                    const id = this.getSeriesId(share.isin)
                    const toRemoveIndex = this.series.findIndex(item => item.id === id)
                    if (toRemoveIndex !== -1) {
                        this.chart.removeSeries(this.series[toRemoveIndex].series)
                        this.series.splice(toRemoveIndex, 1)
                    }
                })
                try {
                    await this.drawChart(isChartTypeSwitch)
                    this.$emit('update:share-data')
                    this.setRefreshTimeout()
                } catch (e) {
                    if (this.$axios.isCancel(e)) {
                        return
                    }
                    // Set timeout even the previous API request was failed as long as it was not a canceled request
                    this.setRefreshTimeout()
                    this.handleBackendException(e, this.$t('component.chart.areaChart.chartUpdateFailed'))
                    return
                }
                this.setPriceFormatter()
                this.setTimeRange()
            },

            reset() {
                this.lastData = {}
                this.isInitializing = true
                this.callClear = true
                this.hasChartData = false
                this.refresh()
            },

            clear() {
                this.series.forEach(item => this.chart.removeSeries(item.series))
                try {
                    this.chart?.remove()
                } catch (e) {
                    console.log(e)
                }
                this.chart = null
                this.series = []
                this.loadStoredBenchmarks()
            },

            setPriceFormatter() {
                const chart = this.getChart()
                if (!chart) {
                    return
                }
                chart.applyOptions({
                    localization: {
                        priceFormatter: this.priceFormatter[this.innerChartType]
                    }
                })
            },

            async drawChart(isChartTypeSwitch = false) {
                let data = null
                try {
                    data = isChartTypeSwitch && this.lastData.data?.graphs.length > this.displayBenchmarks.length
                        ? this.lastData.data
                        : await this.getData(this.source, this.sourceIdentifier, this.displayBenchmarks.map(bm => bm.isin))
                    if (!data) {
                        this.isLoading = false
                        return
                    }
                } catch (e) {
                    //  if the error is request cancelation, need to keep the loading
                    if (this.$axios.isCancel(e)) {
                        throw e
                    }
                    this.isLoading = false
                    return
                }

                if (!this.hideBaseline) {
                    this.drawAsset(this.sourceIdentifier, data.graphs[0])
                }
                for (let i = 0; i < this.displayBenchmarks.length; i++) {
                    const share = this.displayBenchmarks[i]
                    if (share.isShowed ?? true) {
                        const ret = this.drawAsset(share.isin, data.graphs[i + 1])
                        this.displayBenchmarks[i].available = ret !==null
                        this.updateBenchmarks = !this.updateBenchmarks
                        if(ret !== null) {
                            const series = this.getSeries(share.isin)
                            series.applyOptions({
                                color: share.color,
                            })
                        }
                    } else {
                        this.drawAsset(share.isin, null)
                    }
                }
                this.isLoading = false
            },

            drawAsset(sourceIdentifier, rawData) {
                const series = this.getSeries(sourceIdentifier, rawData)
                if (!rawData || rawData.datapoints.length < 1) {
                    series.setData([])
                    return null
                }

                this.setAreaSeriesColors(
                    series,
                    this.innerChartType == 'performance'
                        ? 0
                        : this.getDataFromDataSetByChartType(this.innerChartType, rawData.datapoints[0]),
                    this.getDataFromDataSetByChartType(this.innerChartType, rawData.datapoints[rawData.datapoints.length-1]),
                    sourceIdentifier
                )

                const refinedData = this.refineSeriesData(rawData.datapoints)
                series.setData(refinedData)

                return rawData
            },

            getDataFromDataSetByChartType(type, data) {
                switch (type) {
                    case 'value':
                        return data.c
                    case 'performance':
                        return data.nr
                    case 'simpleReturn':
                        return data.s
                    case 'simpleReturnRel':
                        return data.p
                } 
            },

            refineSeriesData(datasets) {
                if(!datasets) return
                const chartData = []

                for(const ds of datasets) {
                    chartData.push({
                        time: this.getTimezoneCorrectedTime(ds.t, true),
                        value: this.getDataFromDataSetByChartType(this.innerChartType, ds)
                    })
                }

                return chartData
            },

            getSeries(identifier, data = null) {
                const id = this.getSeriesId(identifier)
                // baseline should be the first item in this.series
                const baselineIndex = 0
                let series = this.series.find(item => item.id === id)

                if(series !== undefined) {
                    return series.series
                }

                if(identifier == this.sourceIdentifier) {
                    series = this.addBaselineSeries(data)
                    this.series[baselineIndex] = {
                        id,
                        series,
                    }

                    return series
                }

                // identifier is an ISIN of bendmark
                series = this.addLineSeries(data)
                this.series.push({
                    id,
                    series,
                })

                return series
            },

            addLineSeries(data = null) {
                const chart = this.getChart()
                if (!chart) {
                    return
                }

                let minPrecision = data ?
                    this.getMinPrecision(data)
                    : 0
                
                if (minPrecision === Infinity) {
                    minPrecision = 0
                }

                return chart.addLineSeries({
                    lineWidth: 1,
                    priceFormat: {
                        precision: Math.max(minPrecision, 2),
                        minMove: Math.min(Math.pow(10, -minPrecision), 0.01),
                    }
                })

            },

            getMinPrecision(data) {
                if (!data?.datapoints?.length) {
                    return 0
                }
                const minmax = data.datapoints.map(dp => dp.c).reduce((accu, curr) => {
                    const numberToCompare = curr || 0 // ensure the number to compare is not nullable
                    return [Math.min(numberToCompare, accu[0]), Math.max(numberToCompare, accu[1])]
                }, [Infinity, -Infinity])
                return Math.ceil(Math.abs(Math.log(Math.abs(minmax[1] - minmax[0])) / Math.LN10))
            },

            addBaselineSeries(data = null) {
                const chart = this.getChart()
                if (!chart) {
                    return
                }

                let minPrecision = data ?
                    this.getMinPrecision(data)
                    : 0

                // if minPrecision is Infinity, it will lead to unexpected base
                if (minPrecision === Infinity) {
                    minPrecision = 0
                }

                return chart.addBaselineSeries({
                    lineWidth: 1,
                    priceFormat: {
                        precision: Math.max(minPrecision, 2),
                        minMove: Math.min(Math.pow(10, -minPrecision), 0.01),
                    }
                })
            },

            setAreaSeriesColors(series, open, close, sourceIdentifier) {
                const topRed = 0
                const topGreen = 180
                const topBlue = 150

                const bottomRed = 239
                const bottomGreen = 83
                const bottomBlue = 80

                series.applyOptions({
                    topLineColor: `rgba(${topRed}, ${topGreen}, ${topBlue}, 1)`,
                    topFillColor1: `rgba(${topRed}, ${topGreen}, ${topBlue}, 0.28)`,
                    topFillColor2: `rgba(${topRed}, ${topGreen}, ${topBlue}, 0.05)`,
                    bottomLineColor: `rgba(${bottomRed}, ${bottomGreen}, ${bottomBlue}, 1)`,
                    bottomFillColor1: `rgba(${bottomRed}, ${bottomGreen}, ${bottomBlue}, 0.05)`,
                    bottomFillColor2: `rgba(${bottomRed}, ${bottomGreen}, ${bottomBlue}, 0.28)`,
                    baseValue: {type: 'price', price: open},
                })

                if (this.sourceIdentifier === sourceIdentifier) {
                    this.sourceLineColor = open > close ? `rgb(${bottomRed}, ${bottomGreen}, ${bottomBlue})` : `rgb(${topRed}, ${topGreen}, ${topBlue})`
                }
            },

            setTimeRange() {
                if (!this.chart) {
                    return
                }
                this.chart.timeScale().fitContent()
            },

            getTimezoneCorrectedTime(utcTime, returnAsUnixTimestamp = false) {
                if(isNaN(utcTime)) utcTime = new Date(utcTime)
                if(utcTime instanceof Date) utcTime = utcTime.getTime()/1000

                const timezoneOffsetMinutes = new Date().getTimezoneOffset()
                const correctedTime = utcTime+(-timezoneOffsetMinutes*60)

                if(returnAsUnixTimestamp) return correctedTime
                return new Date(correctedTime*1000)
            },

            getChart() {
                if (this.chart) {
                    return this.chart
                }
                const chartEl = document.getElementById(this.chartId)
                if (!chartEl) {
                    return null
                }
                const chart = createChart(
                    chartEl,
                {
                    height: this.chartHeight,
                    localization: {
                        locale: this.chartLocale,
                        dateFormat: 'dd.MM.yyyy',
                        timeFormatter: time => this.formatCrosshair(time)
                    },
                    handleScale: false,
                    handleScroll: false,
                    ...(this.isDarkMode ? this.chartStyleDark : this.chartStyleLight)
                });

                chart.priceScale('right').applyOptions({
                    scaleMargins: {
                        top: this.source === 'share' ? 0.1 : 0.025,
                        bottom: 0.01,
                    },
                    entireTextOnly: true
                })

                chart.applyOptions({
                    timeScale: {
                        timeVisible: true,
                        tickMarkFormatter: (time, tickMarkType) => this.formatTickMark(time, tickMarkType),
                    },
                    grid: {
                        vertLines: {
                            visible: false,
                        },
                        horzLines: {
                            visible: false,
                        },
                    },
                })
                if (this.source == 'portfolio') {
                    this.subscribeCrosshairMove(chart, chartEl)
                }
                this.chart = chart
                return this.chart
            },

            subscribeCrosshairMove(chart, chartEl) {
                const tooltip = this.$refs['chart-tooltip']
                const toolTipMargin = 30

                chart.subscribeCrosshairMove(param => {
                    let comparisons = []
                    if (
                        param.point === undefined
                            || !param.time
                            || param.point.x < 0
                            || param.point.x > chartEl.clientWidth
                            || param.point.y < 0
                            || param.point.y > chartEl.clientHeight
                    ) {
                        this.tooltip.visible = false
                        return
                    }
                    const y = param.point.y
                    let left = param.point.x + toolTipMargin
                    if (left > chartEl.clientWidth - tooltip.offsetWidth) {
                        left = param.point.x - toolTipMargin - tooltip.offsetWidth
                    }

                    let top = y + toolTipMargin
                    if (top > chartEl.clientHeight - tooltip.offsetHeight) {
                        top = y - tooltip.offsetHeight - toolTipMargin
                    }
                    this.$set(this.tooltip, 'style', {
                        left: `${left}px`,
                        top: `${top}px`,
                    })
                    let dp
                    if (this.lastData.data.graphs.length > 1) {
                        const ts = param.seriesData.get(this.getSeries(this.sourceIdentifier))?.time;
                        dp = this.lastData.times[ts] ?? null;
                        comparisons = this.lastData.comparisonsTimesMap.reduce((acc, cur) => {
                            //return if this comparison is hidden
                            if (this.displayBenchmarks.every((benchmark) => (benchmark.isShowed == false || benchmark.isin !== cur.isin))) {
                                return acc;
                            }
                            acc.push(this.getComparisonInfoByTime(cur, ts))
                            return acc;
                        }, [])
                        this.$set(
                            this.tooltip,
                            'comparisons',
                            comparisons,
                        )
                    } else {
                        dp = this.lastData.data.graphs[0].datapoints[param.logical];
                    }
                    if (!dp) {
                        return
                    }
                    this.$set(
                        this.tooltip,
                        'value',
                        this.format.currency(dp.c, this.currency),
                    )
                    this.$set(
                        this.tooltip,
                        'periodStartValue',
                        this.format.currency(dp.v, this.currency),
                    )
                    this.$set(
                        this.tooltip,
                        'tickIdx',
                        param.logical,
                    )
                    this.$set(
                        this.tooltip,
                        'navChange',
                        this.format.currency(dp.n, this.currency),
                    )
                    this.$set(
                        this.tooltip,
                        'navChangeRel',
                        this.format.percent(dp.nr),
                    )
                    const offset = new Date().getTimezoneOffset() * 60 * 1000
                    const dateStr = this.format.date({
                        date: new Date(offset + param.time * 1000),
                        displayTime: true,
                    })
                    this.$set(this.tooltip, 'date', dateStr)
                    this.$set(
                        this.tooltip,
                        'simpleReturn',
                        this.format.currency(dp.s, this.currency),
                    )
                    this.$set(
                        this.tooltip,
                        'simpleReturnRel',
                        this.format.percent(dp.p),
                    )
                    this.tooltip.visible = true
                });
            },

            setChartWidth() {
                if (this.chart && this.$refs['chartWrapper']) {
                    this.chart.applyOptions({
                        width: this.$refs['chartWrapper'].clientWidth,
                    })
                }
            },

            formatCrosshair(time) {
                const timezoneOffsetMilliSeconds = (new Date().getTimezoneOffset())*60000
                return new Date(time*1000+timezoneOffsetMilliSeconds).toLocaleString(this.chartLocale, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                })
            },

            formatTickMark(time, tickMarkType) {
                const timezoneOffsetMinutes = new Date().getTimezoneOffset()

                time = time+(60*timezoneOffsetMinutes)

                let opt = {}
                switch(tickMarkType) {
                    case TickMarkType.Year:
                        opt = {
                            year: 'numeric'
                        }
                        break
                    case TickMarkType.Month:
                        opt = {
                            month: 'short'
                        }
                        break
                    case TickMarkType.DayOfMonth:
                        opt = {
                            day: '2-digit'
                        }
                        break
                    case TickMarkType.Time:
                        opt = {
                            hour: '2-digit',
                            minute: '2-digit'
                        }
                        break
                    case TickMarkType.TimeWithSeconds:
                        opt = {
                            hour: '2-digit',
                            minute: '2-digit',
                            seconds: '2-digit'
                        }
                }
                return new Date(time*1000).toLocaleString(this.chartLocale, opt)
            },

            toggleSearchbar(e) {
                if(!this.selectIsinMode) {
                    if(e.target.getAttribute("data-role") == "chartSearchbarToggler") {
                        this.selectIsinMode = true
                    }
                    return
                }
                const searchBar = e.target.closest('#areachart_searchbar')
                if (searchBar) {
                    return
                }
                this.selectIsinMode = false;
            },

            async addBenchmark(share) {
                share.color = this.findUniqueColor();
                
                this.benchmarks.push(share)
                this.getDisplayBenchmarks()
                this.selectIsinMode = false
                await this.refresh()
                    .then(() => {
                        if (this.errorMessage) {
                            this.benchmarkError = this.errorMessage
                            this.errorMessage = null
                            setTimeout(() => this.benchmarkError = null, 10000)
                        }
                    })
                this.drawChart(true)
                this.$gtag?.event('sp_chart_compare', {value: this.benchmarks.length})
                this.updateBenchmarksLocalStore()
            },

            findUniqueColor() {
                const existingColors = this.benchmarks.map(benchmark => benchmark.color);
                for (let i = 0; i < this.colors.length; i++) {
                    if (!existingColors.includes(this.colors[i])) {
                        return this.colors[i];
                    }
                }
                const colorIndex = this.benchmarks.length % this.colors.length;
                return this.colors[colorIndex];
            },

            removeBenchmark(share, idx) {
                this.benchmarks.splice(idx, 1)
                this.getDisplayBenchmarks()
                const id = this.getSeriesId(share.isin)
                const toRemoveIndex = this.series.findIndex(item => item.id === id)
                this.chart.removeSeries(this.series[toRemoveIndex].series)
                this.series.splice(toRemoveIndex, 1)
                this.setTimeRange()
                this.updateBenchmarksLocalStore()
            },

            removeAllBenchmarks() {
                this.benchmarks.forEach(share => {
                    const id = this.getSeriesId(share.isin)
                    const toRemoveIndex = this.series.findIndex(item => item.id === id)
                    this.chart.removeSeries(this.series[toRemoveIndex].series)
                    this.series.splice(toRemoveIndex, 1)
                })
                this.benchmarks = []
                this.setTimeRange()
                this.getDisplayBenchmarks()
                this.updateBenchmarksLocalStore()
            },

            getSearchbarBlacklist() {
                const blacklist = this.benchmarks.map(item => item.isin)
                blacklist.push(this.sourceIdentifier)
                return blacklist
            },

            updateErrorMessage() {
                this.errorMessage = ""
            },

            updateChartType(e) {
                if (this.isDiabledButton(e)) {
                    return
                }
                this.$emit('update:chartType', e)
                this.innerChartType = e
                this.refresh(true)
                this.$gtag?.event(`sp_chart_type_${this.innerChartType}`, { value: 1 })
            },

            onIntervalClick(interval) {
                //this.$emit('update:interval', null) // this was a fix for something, do not remove yet, wait some time
                if (this.isFinAgentChart) {
                    this.$store.commit('preferences/setFinAgentStockChartInterval', interval)
                } else if (this.source !== 'market') {
                    this.changeInterval(interval)
                } else {
                    this.$store.commit('preferences/marketAnalysisChartInterval', interval)
                }
                this.$nextTick(() => this.$emit('update:interval', interval))

                // if click to period again, the "interval" prop is not change, so the watch of "interval"
                // need to refresh by hand
                if (interval === this.interval) {
                    this.$root.$emit('kpi-totals-data-updated')
                    this.refresh()
                }
            },

            getCompanyLogoClass(logo) {
                return (logo && logo != null) ? 'company-logo' : 'company-logo-invalid'
            },

            getChangeColor(value) {
                return 'price-change--' + (
                    value?.indexOf('-') == -1
                        ? 'positive'
                        : 'negative'
                )
            },

            handleRefreshChartAndKpi() {
                if (this._isDestroyed) {
                    return
                }
                this.refresh()
            },

            isDiabledButton(key) {
                const shouldBeDisableKeys = ['value', 'simpleReturn']
                if (!shouldBeDisableKeys.includes(key)) {
                    return false
                }

                return this.displayBenchmarks?.length > 0
            },

            priceValueFormatter (price) {
                if (this.priceType && this.priceType === 'PERC') {
                    return this.format.percent(price)
                }

                return this.format.numberToAxisLabel(price, this.lastData.data?.currency || 'EUR')
            },

            getDetailsPageLink(item) {
                if (item.instrumentTypeId === 21 || item.instrumentType === 'Asset') {
                    return ''
                }
                return { name: item.slug ? this.$getTranslatedRouteName('aktie-share') : '', params: { share: item.slug } }
            },

            getSeriesId(identifier) {
                return identifier
            },

            updateChartStyle(isDarkMode) {
                if (this.chart) {
                    this.chart.applyOptions(
                        isDarkMode ? this.chartStyleDark : this.chartStyleLight
                    )
                }
            },
            updateBenchmarksLocalStore() {
                const data = _.cloneDeep(this.benchmarks)
                if (this.source === 'market') {
                    this.$store.commit('chart/setMarketAnalysisBenchmarks', data)
                } else {
                    this.$store.commit('chart/setBenchmarks', data)
                }
            },
            loadStoredBenchmarks() {
                const storedBenchmark = _.cloneDeep(this.source === 'market' ? this.$store.state.chart.marketAnalysisBenchmarks : this.$store.state.chart.benchmarks)
                this.benchmarks = this.isFinAgentChart
                    ? this.initBenchmarks
                    : storedBenchmark.length ? storedBenchmark : this.defaultBenchmarks
                this.getDisplayBenchmarks()
            },
            getDisplayBenchmarks() {
                if (this.source === 'share') {
                    this.displayBenchmarks = this.benchmarks.filter(item => item.isin !== this.sourceIdentifier)
                } else {
                    this.displayBenchmarks = this.benchmarks
                }
            },
            updateFormatterPrivacy() {
                if (this.source == "share" || this.source == "market" || this.isSandboxMode) {
                    return;
                }
                this.format.updatePrivacyEnabled(this.isPrivacyEnabled)
                if (this.priceFormatter) {
                    this.setPriceFormatter();
                }
            },
            handleOpenShareDetail(share) {
                this.$router.push({ name : this.$getTranslatedRouteName('aktie-share'), params: { share: share.slug }})
            },
            handleToggleShowComparison(share) {
                const index = this.benchmarks.findIndex(item => item.isin === share.isin)
                this.benchmarks[index].isShowed = !(this.benchmarks[index].isShowed ?? true)
                this.updateBenchmarksLocalStore()
                this.getDisplayBenchmarks()
                this.refresh(true)
            },
            getOptionIconPath(isShowed) {
                if (this.$vuetify.theme.dark) {
                    return require('@/assets/icons/options-dark.svg')
                }
                return require(`@/assets/icons/options-${(isShowed ?? true) ? 'dark' : 'light'}.svg`)
            },
            cancelActiveRequest() {
                if (this.activeRequestGetChartData) {
                    this.activeRequestGetChartData.cancel();
                }
            }
        },

        watch: {
            refreshSwitch() {
                this.handleRefreshChartAndKpi()
            },
            interval() {
                this.refresh()
                this.$gtag?.event(`sp_chart_interval_${this.interval}`, { value: 1 })
            },
            currency(_, oldCurrency) {
                if (oldCurrency === null) {
                    return
                }
                if (!this.isLoading) {
                    this.refresh()
                }
            },
            sourceIdentifier() {
                this.reset()
            },
            isVisible(current, previous) {
                if (!previous && current) {
                    this.refresh()
                }
            },
            isPrivacyEnabled: {
                handler() {
                    this.updateFormatterPrivacy()
                },
                immediate: true
            },
            isSandboxMode: {
                handler() {
                    this.updateFormatterPrivacy()
                },
                immediate: true
            },
            isDarkMode: {
                immediate: true,
                handler(newState) {
                    this.updateChartStyle(newState)
                }
            },
            innerChartType(newChartType) {
                this.$emit('update:chartType', newChartType)
            },
            '$store.state.preferences.isSandboxMode': {
                handler(newState) {
                    this.chartService.setSandboxMode(newState)
                }
            },
            benchmarks(items) {
                const relativeChartTypes = ['performance', 'simpleReturnRel'];
                if (items.length > 0 && !relativeChartTypes.includes(this.innerChartType)) {
                    this.innerChartType = 'performance';
                }
            }
        },

        mounted() {
            this.chartService = new ChartService(this.$axios, this.$store.state.preferences.isSandboxMode)
            this.priceFormatter = {
                value: this.priceValueFormatter,
                performance: price => this.format.percent(price),
                simpleReturn: price => this.format.numberToAxisLabel(price, this.lastData.data?.currency || 'EUR'),
                simpleReturnRel: price => this.format.percent(price)
            }
            this.loadStoredBenchmarks()
            this.refresh()
            window.addEventListener('resize', this.setChartWidth)
            this.assetMethodsIcons = this.format.getAssetMethodIcons()
        },

        beforeMount() {
            if (!this.isReClicking) {
                this.isInitializing = true
            }
            window.addEventListener('click', this.toggleSearchbar);
            this.$root.$on('requestPortfolioPositionRendering', this.refresh)
            this.searchbarBus = new Vue()
            this.searchbarBus.$on('searchbar-share-selected', this.addBenchmark);
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.setChartWidth)
            window.removeEventListener('click', this.toggleSearchbar)
            this.$root.$off('requestPortfolioPositionRendering', this.refresh)
            this.searchbarBus.$off('searchbar-share-selected', this.addBenchmark)
            this.clearActiveRefreshTimeout()
            this.clear()
            this.cancelActiveRequest();
        }
    }
